import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paypalOrderId = searchParams.get("paypal_order_id");
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    // PayPal: capture already completed on the previous page — just show success.
    if (paypalOrderId) {
      setStatus("success");
      return;
    }
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 8;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("failed");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(res.data);
          return;
        } else if (res.data.status === "expired") {
          setStatus("failed");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, paypalOrderId]);

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center" data-testid="checkout-success-page">
      <div className="container-custom max-w-xl text-center py-20">
        {status === "loading" && (
          <div className="animate-fade-in">
            <Loader2 className="animate-spin text-[#b8966a] mx-auto mb-6" size={48} />
            <h1 className="font-heading text-2xl text-[#f8f5f0] mb-4">
              Processing Your Order
            </h1>
            <p className="font-body text-[#a19d98]">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 border border-[#b8966a] rounded-full flex items-center justify-center">
              <CheckCircle className="text-[#b8966a]" size={40} />
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#f8f5f0] mb-4">
              Thank You
            </h1>
            <p className="font-body text-lg text-[#a19d98] mb-8">
              Your order has been confirmed. We'll begin preparing your arrangement 
              and send you a confirmation email shortly.
            </p>
            
            {paymentData && (
              <div className="bg-[#161616] border border-[#b8966a]/10 p-6 mb-8 text-left">
                <p className="overline mb-4">Order Details</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-body text-[#a19d98]">Amount Paid</span>
                    <span className="font-sans text-[#f8f5f0]">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-[#a19d98]">Status</span>
                    <span className="font-sans text-[#b8966a] uppercase text-sm">
                      {paymentData.payment_status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/collection" className="btn-secondary" data-testid="continue-shopping">
                Continue Shopping
              </Link>
              <Link to="/" className="btn-primary" data-testid="return-home">
                Return Home
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 border border-red-500/50 rounded-full flex items-center justify-center">
              <XCircle className="text-red-500" size={40} />
            </div>
            <h1 className="font-display text-3xl text-[#f8f5f0] mb-4">
              Payment Issue
            </h1>
            <p className="font-body text-[#a19d98] mb-8">
              We couldn't confirm your payment. This may be due to a cancelled transaction 
              or a temporary issue. Please try again or contact us for assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/collection" className="btn-secondary" data-testid="try-again">
                Try Again
              </Link>
              <Link to="/inquiries" className="btn-primary" data-testid="contact-us">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
